import { isPresent } from "@apl-digital/utils";
import { createSlice, type PayloadAction } from "@reduxjs/toolkit";

import { CartStatus, type ICartLayout } from "@/api/interfaces/cartLayouts";
import { getStorage, StorageKey } from "@/constants/storage";
import {
  hasData,
  setStateFailed,
  setStatePending,
} from "@/store/apiRequestStatusMachine";

import { isStatus } from "../utils";

import {
  addProductToCart,
  bundleShoppingCart,
  initializeDiningCart,
  refreshCart,
  removeCustomerFromCart,
  removeLinesFromCart,
  unbundleShoppingCart,
  updateCustomerCart,
} from "./actions";
import type { ShoppingCartState } from "./types";

const restoreCartDataOrSetFailed = (
  currentCart: ShoppingCartState["shoppingCart"],
  setCart: (nextCart: ShoppingCartState["shoppingCart"]) => void,
): void => {
  if (hasData(currentCart)) {
    setCart({
      status: "succeeded",
      state: currentCart.state,
    });
    return;
  }

  setStateFailed<ICartLayout>(currentCart, setCart);
};

const getInitialState = (loadFromStorage: boolean): ShoppingCartState => {
  const storedCart = getStorage(StorageKey.CART);

  return {
    shoppingCart: loadFromStorage
      ? isPresent(storedCart)
        ? { status: "stale", state: JSON.parse(storedCart) as ICartLayout }
        : { status: "idle" }
      : { status: "idle" },
    newShoppingCart: { status: "idle" },
    shoppingCartLanguage: loadFromStorage
      ? getStorage(StorageKey.SHOPPING_CART_LANGUAGE)
      : null,
  };
};

export const shoppingCartSlice = createSlice({
  name: "shoppingCart",
  initialState: getInitialState(true),
  reducers: {
    resetShoppingCartSlice: () => {
      return getInitialState(false);
    },
    resetShoppingCart: (state) => {
      state.shoppingCart = getInitialState(false).shoppingCart;
    },
    resetNewShoppingCart: (state) => {
      state.newShoppingCart = getInitialState(false).newShoppingCart;
    },
    shoppingCartLanguageChanged: (
      state,
      { payload }: PayloadAction<string>,
    ) => {
      state.shoppingCartLanguage = payload;
    },
    promoteNewShoppingCart: (state) => {
      state.shoppingCart = state.newShoppingCart;
      state.newShoppingCart = { status: "idle" };
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(removeCustomerFromCart.pending, (state) => {
        setStatePending<ICartLayout>(
          state.shoppingCart,
          (nextState) => (state.shoppingCart = nextState),
        );
      })
      .addCase(removeCustomerFromCart.fulfilled, (state, { payload }) => {
        state.shoppingCart = { status: "succeeded", state: payload };
      })
      .addCase(removeCustomerFromCart.rejected, (state) => {
        setStateFailed<ICartLayout>(
          state.shoppingCart,
          (nextState) => (state.shoppingCart = nextState),
        );
      });

    builder
      .addCase(updateCustomerCart.pending, (state) => {
        setStatePending<ICartLayout>(
          state.shoppingCart,
          (nextState) => (state.shoppingCart = nextState),
        );
      })
      .addCase(updateCustomerCart.fulfilled, (state, { payload }) => {
        state.shoppingCart = { status: "succeeded", state: payload };
      })
      .addCase(updateCustomerCart.rejected, (state) => {
        setStateFailed<ICartLayout>(
          state.shoppingCart,
          (nextState) => (state.shoppingCart = nextState),
        );
      });

    builder
      .addCase(initializeDiningCart.pending, (state) => {
        setStatePending<ICartLayout>(
          state.newShoppingCart,
          (nextState) => (state.newShoppingCart = nextState),
        );
      })
      .addCase(initializeDiningCart.fulfilled, (state, { payload }) => {
        state.newShoppingCart = { status: "succeeded", state: payload };
      })
      .addCase(initializeDiningCart.rejected, (state) => {
        setStateFailed<ICartLayout>(
          state.newShoppingCart,
          (nextState) => (state.newShoppingCart = nextState),
        );
      });

    builder
      .addCase(bundleShoppingCart.pending, (state) => {
        setStatePending<ICartLayout>(
          state.shoppingCart,
          (nextState) => (state.shoppingCart = nextState),
        );
      })
      .addCase(bundleShoppingCart.fulfilled, (state, { payload }) => {
        state.shoppingCart = { status: "succeeded", state: payload };
      })
      .addCase(bundleShoppingCart.rejected, (state) => {
        setStateFailed<ICartLayout>(
          state.shoppingCart,
          (nextState) => (state.shoppingCart = nextState),
        );
      });

    builder
      .addCase(unbundleShoppingCart.pending, (state) => {
        setStatePending<ICartLayout>(
          state.shoppingCart,
          (nextState) => (state.shoppingCart = nextState),
        );
      })
      .addCase(unbundleShoppingCart.fulfilled, (state, { payload }) => {
        state.shoppingCart = { status: "succeeded", state: payload };
      })
      .addCase(unbundleShoppingCart.rejected, (state) => {
        setStateFailed<ICartLayout>(
          state.shoppingCart,
          (nextState) => (state.shoppingCart = nextState),
        );
      });

    builder
      .addCase(refreshCart.pending, (state, { meta }) => {
        if (meta.arg.transactionId === state.newShoppingCart.state?.ID) {
          setStatePending<ICartLayout>(
            state.newShoppingCart,
            (nextState) => (state.newShoppingCart = nextState),
          );
        } else {
          setStatePending<ICartLayout>(
            state.shoppingCart,
            (nextState) => (state.shoppingCart = nextState),
          );
        }
      })
      .addCase(refreshCart.fulfilled, (state, { payload, meta }) => {
        if (meta.arg.transactionId === state.newShoppingCart.state?.ID) {
          state.newShoppingCart = { status: "succeeded", state: payload };
        } else {
          state.shoppingCart = { status: "succeeded", state: payload };
        }
      })
      .addCase(refreshCart.rejected, (state, { meta }) => {
        if (meta.arg.transactionId === state.newShoppingCart.state?.ID) {
          restoreCartDataOrSetFailed(
            state.newShoppingCart,
            (nextState) => (state.newShoppingCart = nextState),
          );
        } else {
          restoreCartDataOrSetFailed(
            state.shoppingCart,
            (nextState) => (state.shoppingCart = nextState),
          );
        }
      });

    builder
      .addCase(addProductToCart.pending, (state, { meta }) => {
        if (meta.arg.transactionId === state.newShoppingCart.state?.ID) {
          setStatePending<ICartLayout>(
            state.newShoppingCart,
            (nextState) => (state.newShoppingCart = nextState),
          );
        } else {
          setStatePending<ICartLayout>(
            state.shoppingCart,
            (nextState) => (state.shoppingCart = nextState),
          );
        }
      })
      .addCase(addProductToCart.rejected, (state, { meta }) => {
        if (meta.arg.transactionId === state.newShoppingCart.state?.ID) {
          restoreCartDataOrSetFailed(
            state.newShoppingCart,
            (nextState) => (state.newShoppingCart = nextState),
          );
        } else {
          restoreCartDataOrSetFailed(
            state.shoppingCart,
            (nextState) => (state.shoppingCart = nextState),
          );
        }
      });

    builder
      .addCase(removeLinesFromCart.pending, (state) => {
        setStatePending<ICartLayout>(
          state.shoppingCart,
          (nextState) => (state.shoppingCart = nextState),
        );
      })
      .addCase(removeLinesFromCart.rejected, (state) => {
        restoreCartDataOrSetFailed(
          state.shoppingCart,
          (nextState) => (state.shoppingCart = nextState),
        );
      });
  },
  selectors: {
    selectShoppingCart: (state) => state.shoppingCart,
    selectNewShoppingCart: (state) => state.newShoppingCart,
    selectShoppingCartIsLoaded: (state) =>
      isStatus(state.shoppingCart, "succeeded") &&
      isStatus(state.newShoppingCart, "idle"),
    selectCurrentBarcodeWithoutChecksum: (state): string | undefined =>
      hasData(state.shoppingCart)
        ? state.shoppingCart.state.BarcodeWithoutChecksum
        : undefined,
    selectCurrentTransactionId: (state): number | undefined =>
      hasData(state.shoppingCart) ? state.shoppingCart.state.ID : undefined,
    selectCanEditShoppingCart: (state): boolean =>
      hasData(state.shoppingCart) &&
      state.shoppingCart.state.Status !== CartStatus.ON_HOLD,
    selectCheckoutOptions: (state) =>
      hasData(state.shoppingCart)
        ? state.shoppingCart.state.CheckoutOptions
        : [],
    selectShoppingCartLines: (state) =>
      hasData(state.shoppingCart) && state.shoppingCart.state.Lines
        ? state.shoppingCart.state.Lines
        : undefined,
    selectShoppingCartSums: (state) =>
      hasData(state.shoppingCart) ? state.shoppingCart.state.Sums : undefined,
    selectShoppingCartContactInfo: (state) =>
      hasData(state.shoppingCart)
        ? state.shoppingCart.state.ContactInfo
        : undefined,
    selectIsPreOrder: (state) =>
      hasData(state.shoppingCart) &&
      state.shoppingCart.state.Defaults?.dttmRequestedDelivery !== null,
  },
});

export const {
  resetShoppingCartSlice,
  resetShoppingCart,
  resetNewShoppingCart,
  shoppingCartLanguageChanged,
  promoteNewShoppingCart,
} = shoppingCartSlice.actions;

export const {
  selectShoppingCart,
  selectNewShoppingCart,
  selectCurrentBarcodeWithoutChecksum,
  selectCurrentTransactionId,
  selectCanEditShoppingCart,
  selectCheckoutOptions,
  selectShoppingCartLines,
  selectShoppingCartSums,
  selectShoppingCartContactInfo,
  selectIsPreOrder,
  selectShoppingCartIsLoaded,
} = shoppingCartSlice.selectors;

export default shoppingCartSlice.reducer;
